import React, {useState, useEffect, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {
    FileBarChart,
    ChevronRight,
    MessageCircle,
    Atom,
} from "lucide-react";

import {useUser} from "../context/UserContext";
import DataReportsDialog from "./components/DataReportsDialog";
import WhatsAppBiQSection from "./components/WhatsAppBiQSection";
import MultiSelectCommoditySelector from "./components/MultiSelectCommoditySelector";
import WeatherMap from "./components/home/WeatherMap";
import WeatherDigestAlerts from "./components/home/WeatherDigestAlerts";
import { useGlobalFontSize } from "../context/GlobalFontSizeContext";
import { trackEvent, EVENTS } from "../utils/analytics";
import { setPreferredCommodities } from "../utils/api";

const Home = () => {
    return <HomeContent />;
};

const HomeContent = () => {
    const {t} = useTranslation();
    const {userDetails, email, refetchUserDetails} = useUser();
    const userName = userDetails?.name || t('common.user');
    const navigate = useNavigate();
    const [showDataReportsDialog, setShowDataReportsDialog] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [selectedCommodities, setSelectedCommodities] = useState(userDetails?.preferred_commodities || []);
    const [isSavingCommodities, setIsSavingCommodities] = useState(false);
    const {fontSize} = useGlobalFontSize();

    // Deduplicate commodities (case-insensitive), preferring the first occurrence
    const deduplicatedCommodities = useMemo(() => {
        const seen = new Set();
        return selectedCommodities.filter(c => {
            const lower = c.toLowerCase();
            if (seen.has(lower)) return false;
            seen.add(lower);
            return true;
        });
    }, [selectedCommodities]);

    // Sync state when userDetails.preferred_commodities changes (but not while saving)
    useEffect(() => {
        if (userDetails?.preferred_commodities && !isSavingCommodities) {
            setSelectedCommodities(userDetails.preferred_commodities);
        }
    }, [userDetails?.preferred_commodities, isSavingCommodities]);

    const handleCommodityChange = async (newCommodities) => {
        trackEvent(EVENTS.PREFERRED_COMMODITIES_UPDATED, { count: newCommodities.length });
        setSelectedCommodities(newCommodities);
        setIsSavingCommodities(true);

        try {
            await setPreferredCommodities(newCommodities);
            refetchUserDetails();
        } catch (error) {
            console.error('Failed to save preferred commodities:', error);
        } finally {
            setIsSavingCommodities(false);
        }
    };

    const handleQuickReportsClick = () => {
        trackEvent(EVENTS.QUICK_REPORTS_CLICK);
        setShowDataReportsDialog(true);
    };


    const handleChatWithBiQ = () => {
        navigate('/biq');
    };

    const handleDeepThinking = () => {
        navigate('/biq', { state: { deepThinking: true } });
    };

    const handleAddCommodities = () => {
        navigate('/analytics');
    };

    const handleConfigureAlerts = () => {
        navigate('/alerts');
    };

    const handleGenerateReport = async (commodity) => {
        if (!email) {
            console.error("No email available");
            return;
        }

        setIsGeneratingReport(true);

        try {
            // Navigate to BiQ page and generate report there
            navigate("/biq", {
                state: {
                    generateReport: true,
                    commodity: commodity,
                },
            });
        } catch (error) {
            console.error("Error generating report:", error);
        } finally {
            setIsGeneratingReport(false);
            setShowDataReportsDialog(false);
        }
    };

    return (
        <div className="pr-[16px] sm:pr-[24px] max-w-full overflow-x-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-[12px] sm:gap-[16px] mb-[20px] sm:mb-[24px] md:mb-[32px]">
                <div className="flex items-center gap-[12px] min-w-0">
                    <div className="min-w-0">
                        <h1 className="font-semibold text-[#282A29] dark:text-[#ffffff] mb-[4px] truncate" style={{fontSize: fontSize + 4}}>
                            {t('home.greeting', {name: userName})}
                        </h1>
                    </div>
                </div>

                <div className="flex items-center gap-[8px] sm:gap-[12px] overflow-x-auto scrollbar-hide flex-shrink-0 -mx-[16px] px-[16px] sm:-mx-[24px] sm:px-[24px] md:mx-0 md:px-0">
                    <QuickReportsButton
                        icon={
                            <FileBarChart
                                size={18}
                                className="text-[#282A29] dark:text-[#ffffff] sm:w-5 sm:h-5"
                            />
                        }
                        label={t('home.quickReports')}
                        onClick={handleQuickReportsClick}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-[12px] sm:gap-y-[16px] gap-x-[16px] sm:gap-x-[24px] pb-10">
                <div className="md:col-span-2">
                    <FeatureCard
                        title={t('home.meetBiq.title')}
                        description={t('home.meetBiq.description')}
                        titleSize="h2"
                        buttons={[
                            {
                                label: t('home.meetBiq.speakButton'),
                                onClick: handleChatWithBiQ,
                                icon: <MessageCircle size={18} />,
                            },
                            {
                                label: t('home.meetBiq.deepThinkingButton'),
                                onClick: handleDeepThinking,
                                icon: <Atom size={18} />,
                                badge: <NewBadge />,
                                subtitle: t('home.meetBiq.deepThinkingSubtitle'),
                            },
                        ]}
                    />
                </div>
                <FeatureCard
                    title={t('home.bloomTerminal.title')}
                    description={t('home.bloomTerminal.description')}
                    buttons={[
                        {
                            label: t('home.bloomTerminal.button'),
                            onClick: handleAddCommodities,
                        },
                    ]}
                />
                <FeatureCard
                    title={t('home.setUpPriceAlerts.title')}
                    description={t('home.setUpPriceAlerts.description')}
                    buttons={[
                        {
                            label: t('home.setUpPriceAlerts.button'),
                            onClick: handleConfigureAlerts,
                        },
                    ]}
                />
                <WhatsAppBiQSection />
                {/* Commodity selector card */}
                <div className="border border-[#C3C4C3] dark:border-[#828382] rounded-lg p-[16px] bg-white dark:bg-[#191919]">
                    <h3 className="font-semibold text-bloom-green mb-[12px]" style={{fontSize: fontSize + 2}}>
                        {t('home.selectCommodities.title')}
                    </h3>
                    <p className="text-[#5D625F] dark:text-[#828382] mb-[16px] leading-relaxed" style={{fontSize: fontSize}}>
                        {t('home.selectCommodities.description')}
                    </p>
                    <MultiSelectCommoditySelector
                        values={deduplicatedCommodities}
                        onChange={handleCommodityChange}
                        placeholder={t('home.selectCommodities.placeholder', 'Select commodities...')}
                    />
                </div>
                {/* Weather Digest Alerts - full width */}
                <div className="md:col-span-2">
                    <WeatherDigestAlerts commodities={deduplicatedCommodities} />
                </div>
                {/* Weather Map - full width */}
                <div className="md:col-span-2">
                    <WeatherMap commodities={deduplicatedCommodities} />
                </div>
            </div>

            <DataReportsDialog
                isOpen={showDataReportsDialog}
                onClose={() => setShowDataReportsDialog(false)}
                onConfirm={handleGenerateReport}
                isLoading={isGeneratingReport}
            />
        </div>
    );
};

const QuickReportsButton = ({icon, label, onClick}) => {
    const {fontSize} = useGlobalFontSize();
    return (
        <button
            className="flex items-center gap-[6px] sm:gap-[8px] px-[10px] sm:px-[12px] py-[8px] sm:py-[10px] rounded-lg bg-white dark:bg-[#191919] border border-[#828382] group hover:bg-gray-50 dark:hover:bg-[#2f312f] whitespace-nowrap flex-shrink-0 transition-colors"
            onClick={onClick}
        >
            {icon}
            <span className="text-[#282A29] dark:text-[#ffffff] text-xs sm:text-sm" style={{fontSize: fontSize - 2}}>
                {label}
            </span>
            <ChevronRight
                size={14}
                className="text-[#282A29] dark:text-[#ffffff] sm:w-4 sm:h-4"
            />
        </button>
    );
};

// New Badge Component
const NewBadge = () => {
    const {fontSize} = useGlobalFontSize();
    return (
        <span 
            className="px-[8px] py-[2px] rounded-[4px] bg-purple-500/20 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400 text-xs font-medium"
            style={{fontSize: fontSize - 4}}
        >
            New
        </span>
    );
};

const InfoCard = ({ title, content = [], buttonLabel, onButtonClick }) => {
    const {fontSize} = useGlobalFontSize();
    
    return (
        <div className="border border-[#C3C4C3] dark:border-[#828382] rounded-lg p-[16px] bg-white dark:bg-[#191919]">
            <h3 className="font-semibold text-bloom-green mb-[12px]" style={{fontSize: fontSize + 2}}>
                {title}
            </h3>
            <div className="flex flex-col items-center justify-center min-h-[194px] bg-[#F0F5F2] dark:bg-[#232121] rounded-[4px] p-[16px]">
                {content.map((line, index) => (
                    <p 
                        key={index}
                        className="font-semibold text-[#5D625F] dark:text-[#828382] text-center leading-relaxed"
                        style={{fontSize: fontSize}}
                    >
                        {line}
                    </p>
                ))}
                {buttonLabel && (
                    <div className="flex justify-center mt-[16px]">
                        <button
                            onClick={onButtonClick || (() => {})}
                            className="flex items-center justify-center gap-[8px] py-[12px] px-[16px] rounded-lg bg-[#E8EBE9] dark:bg-[#282a29] hover:bg-bloom-green dark:hover:bg-bloom-green text-[#5D625F] dark:text-[#828382] hover:text-[#f0f5f2] dark:hover:text-[#f0f5f2] font-medium transition-colors whitespace-nowrap"
                            style={{fontSize: 12}}
                        >
                            {buttonLabel}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const FeatureCard = ({ title, description, icon, buttons = [], className = '', titleSize = 'h3' }) => {
    const {fontSize} = useGlobalFontSize();
    const TitleTag = titleSize === 'h2' ? 'h2' : 'h3';
    const titleSizeClass = titleSize === 'h2' ? fontSize + 2 : fontSize + 2;
    
    return (
        <div className={`border border-[#C3C4C3] dark:border-[#828382] rounded-lg p-[16px] bg-white dark:bg-[#191919] ${className}`}>
            {icon ? (
                <div className="flex items-center gap-[8px] mb-[12px]">
                    {icon}
                    <TitleTag className="font-semibold text-bloom-green" style={{fontSize: titleSizeClass}}>
                        {title}
                    </TitleTag>
                </div>
            ) : (
                <TitleTag className="font-semibold text-bloom-green mb-[12px]" style={{fontSize: titleSizeClass}}>
                    {title}
                </TitleTag>
            )}
            <p className="text-[#5D625F] dark:text-[#828382] mb-[16px] leading-relaxed" style={{fontSize: fontSize}}>
                {description}
            </p>
            {buttons.length > 0 && (
                <div className={`flex ${buttons.length > 1 ? 'flex-col sm:flex-row' : ''} gap-[16px]`}>
                    {buttons.map((button, index) => (
                        <div key={index} className="flex flex-col">
                            <button
                                onClick={button.onClick}
                                className="flex items-center justify-start gap-[8px] py-[12px] px-[16px] rounded-lg bg-[#F0F5F2] dark:bg-[#282a29] hover:bg-bloom-green dark:hover:bg-bloom-green text-[#5D625F] dark:text-[#828382] hover:text-[#f0f5f2] dark:hover:text-[#f0f5f2] font-medium transition-colors whitespace-nowrap group [&_svg]:text-[#5D625F] dark:[&_svg]:text-[#828382] [&:hover_svg]:text-[#f0f5f2]"
                                style={{fontSize: fontSize - 2}}
                            >
                                {button.icon && button.icon}
                                {button.label}
                                {button.badge && button.badge}
                            </button>
                            {button.subtitle && (
                                <span className="text-[#828382] dark:text-[#6b6b6b] mt-[6px] text-center italic" style={{fontSize: fontSize - 4}}>
                                    {button.subtitle}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Home;
